import { gql } from '@apollo/client';
import { genericFields, profileItemStatusCounts } from '../fragments';
import { graphql } from '../../graphql';

export const companyNameQuery = graphql(`
  query CompanyName {
    profile {
      ... on CompanyProfile {
        name
        id
        phone
        email
        isRestricted
        businessNotifications {
          description
          id
          imageUrl
          key
          title
        }
      }
    }
  }
`);

export const userDataQuery = graphql(`
  query UserData {
    profile {
      ... on CompanyProfile {
        name
        id
        phone
        email
        targetType
        isRestricted
      }
    }
  }
`);

export const profileBalanceQuery = graphql(`
  query ProfileBalance {
    profile {
      ... on CompanyProfile {
        balance {
          item {
            paid {
              total
            }
          }
        }
      }
    }
  }
`);

export const getProfileDataQuery = graphql(`
  query Profile {
    profile {
      ... on CompanyProfile {
        id
        name
        email
        phone
        alternativePhone
        hasUnpaidInvoice
        targetType
        balance {
          item {
            free
            nextFreeAt
            paid {
              main
              pack
              total
            }
          }
          personalAccount {
            bonus
            cash
            pack
            total
          }
        }
        isBusiness
        business {
          ... on Agency {
            id
            name
          }
          ... on Residence {
            id
            name
          }
        }
        paidServices {
          id
          activeUntil
          title
          targetType
          options {
            id
            name
            price
            priceWithDiscount(paymentMethodId: "wallet")
          }
          paymentMethods {
            id
            balance
            name
            cardToken
            iconUrl
            isAppleGooglePayEnabled
          }
        }
        businessNotifications {
          description
          id
          imageUrl
          key
          title
        }
        lastOperations {
          ... on Payment {
            id
            amount
            historyTotal: total
            createdAt
            discount
            paidServiceOptionName
            paymentMethodName
            item {
              id
              title
              isPaidDaily
              isLeased
              price {
                total
                currency
              }
              photos(limit: 1) {
                full
                large
                f660x496
                large
              }
              location {
                fullName
              }
            }
          }
          ... on PersonalAccountTransaction {
            id
            accountTotal: total
            createdAt
            payment {
              id
              amount
              total
              createdAt
              discount
              paidServiceOptionName
              paymentMethodName
              item {
                id
                title
                isPaidDaily
                isLeased
                price {
                  total
                  currency
                }
                photos(limit: 1) {
                  full
                  f660x496
                  large
                }
                location {
                  fullName
                }
              }
            }
          }
          ... on ItemBalanceTransaction {
            id
            createdAt
            itemTotal: total
            source {
              ... on Item {
                id
                title
                location {
                  fullName
                }
              }
              ... on Payment {
                id
                amount
                createdAt
                paidServiceOptionName
                paymentMethodName
              }
            }
          }
        }
      }
    }
  }
`);

export const profileStateItemsQuery = gql`
  query ProfileStateItems (
    $first: Int, $after: String, $state: ProfileItemTabStatesEnum
  ) {
    profile {
      ... on CompanyProfile {
        id
        isBusiness
        ${profileItemStatusCounts}
        items (
        state: $state, first: $first, after: $after
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              state
              ${genericFields}
              hasInternalLoan
              photos(limit: 4) {
                thumbnail
                f460x345
                large
              }
            }
          }
        }
      }
    }
  }
`;

export const profileActivePackagesQuery = graphql(`
  query ProfileActivePackages {
    invoices(scope: ACTIVE) {
      edges {
        node {
          id
          amount
          additionalConditions
          package {
            discount
            name
            paidAdsCount
            walletAmount
          }
          expiresAt
          startsAt
          status
          vatAmount
          paidAt
        }
      }
    }
    packages {
      name
      discount
      paidAdsCount
      price
      walletAmount
      periods {
        price
        value
      }
    }
  }
`);

export const profileInvoicesQuery = graphql(`
  query ProfileInvoices(
    $first: Int
    $scope: InvoiceScopeEnum
    $cursor: String
    $platform: PlatformEnum
  ) {
    invoices(first: $first, scope: $scope, after: $cursor) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          amount
          additionalConditions
          package {
            discount
            name
            paidAdsCount
            walletAmount
          }
          payment {
            paymentMethodName
            createdAt
          }
          expiresAt
          startsAt
          status
          vatAmount
          printUrl
          paidAt
          contractNumber
          billingInfo {
            companyName
            companyTin
            id
          }
          paidServices {
            id
            activeUntil
            title
            targetType
            options {
              id
              name
              price
              priceWithDiscount(paymentMethodId: "wallet")
            }
            paymentMethods(platform: $platform) {
              id
              balance
              name
              cardToken
              iconUrl
              isAppleGooglePayEnabled
            }
          }
        }
      }
    }
  }
`);

export const profileTariffsQuery = graphql(`
  query ProfileTariffs {
    packages {
      name
      discount
      paidAdsCount
      price
      walletAmount
      periods {
        price
        value
      }
    }
  }
`);

export const profilePaidServiceOptionsQuery = gql`
  query ProfilePaidServiceOptions {
    bump: paidServiceOptions(paidServiceId: "bump") {
      ...PaidServiceFields
    }
    vip: paidServiceOptions(paidServiceId: "vip") {
      ...PaidServiceFields
    }
    feature: paidServiceOptions(paidServiceId: "feature") {
      ...PaidServiceFields
    }
  }

  fragment PaidServiceFields on PaidServiceOption {
    id
    name
    price
  }
`;

export const profilePaymentHistoryQuery = graphql(`
  query ProfilePaymentHistory($first: Int, $cursor: String) {
    paymentHistory(first: $first, after: $cursor) {
      totalCount
      edges {
        node {
          amount
          total
          createdAt
          discount
          id
          item {
            id
            title
            isPaidDaily
            isLeased
            price {
              total
              currency
            }
            photos(limit: 1) {
              full
              f660x496
              large
            }
            location {
              fullName
            }
          }
          paidServiceOptionName
          paymentMethodName
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

export const profileAccountBalanceHistoryQuery = graphql(`
  query ProfileAccountBalanceHistory($first: Int, $cursor: String) {
    personalAccountBalanceHistory(first: $first, after: $cursor) {
      totalCount
      edges {
        node {
          id
          total
          createdAt
          payment {
            id
            amount
            createdAt
            discount
            paidServiceOptionName
            paymentMethodName
            item {
              id
              title
              isPaidDaily
              isLeased
              price {
                total
                currency
              }
              photos(limit: 1) {
                full
                f660x496
                large
              }
              location {
                fullName
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

export const profilePaidAdsTransactionsQuery = graphql(`
  query ProfilePaidAdsTransactions($first: Int, $cursor: String) {
    itemBalanceHistory(first: $first, after: $cursor) {
      totalCount
      edges {
        node {
          id
          createdAt
          total
          source {
            ... on Item {
              id
              title
              location {
                fullName
              }
            }
            ... on Payment {
              id
              amount
              createdAt
              paidServiceOptionName
              paymentMethodName
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

export const agencyInfoQuery = graphql(`
  query AgencyInfo {
    profile {
      ... on CompanyProfile {
        agency {
          ... on AgencyProfile {
            contractDuration {
              startDate
              endDate
            }
            email
            name
            paidItemBalance
            personalAccountTotalBalance
            phones
          }
        }
      }
    }
  }
`);

export const agencyAgentsQuery = graphql(`
  query AgencyAgents {
    profile {
      ... on CompanyProfile {
        agency {
          ... on AgencyProfile {
            agents {
              __typename
              ... on Agent {
                id
                name
                email
                phone
                isMain
                paidItemBalance
                personalAccountTotalBalance
                publishedItemsCount
              }
              ... on PendingAgent {
                id
                pendingEmail: email
              }
            }
          }
        }
      }
    }
  }
`);

export const agencyTransfersQuery = graphql(`
  query AgencyTransfers($first: Int, $after: String) {
    profile {
      ... on CompanyProfile {
        agency {
          ... on AgencyProfile {
            balanceTransfers(first: $first, after: $after) {
              totalCount
              pageInfo {
                hasNextPage
                endCursor
              }
              edges {
                node {
                  id
                  createdAt
                  paidItemAmount
                  personalAccountAmount
                  senderEmail
                  receiverEmail
                }
              }
            }
          }
        }
      }
    }
  }
`);
